import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, LogIn, LogOut, MessageCircle, XCircle, Copy, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import WhatsAppRepliesEditor from '../components/WhatsAppRepliesEditor';
import WhatsAppAppointmentNotifications from '../components/WhatsAppAppointmentNotifications';
import WhatsAppAIOptions from '../components/WhatsAppAIOptions';
import WhatsAppGeneralSettings from '../components/WhatsAppGeneralSettings';
import './WhatsAppSettings.css';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const uploadUrl = process.env.REACT_APP_UPLOAD_URL || 'http://localhost:5002';
const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function WhatsAppSettings({ standalone = false }) {
  const [activeTab, setActiveTab] = useState('status');
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [authDialog, setAuthDialog] = useState(false);
  const [authMethod, setAuthMethod] = useState('qr');
  const [authPhone, setAuthPhone] = useState('');
  const [phoneCountry, setPhoneCountry] = useState('kz');
  const [authChallenge, setAuthChallenge] = useState(null);
  const [previousCode, setPreviousCode] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authStarted, setAuthStarted] = useState(false);
  const [qrImageLoaded, setQrImageLoaded] = useState(false);
  const [settingsSection, setSettingsSection] = useState('reply-template');

  // 👉 НОВОЕ СОСТОЯНИЕ ДЛЯ РОЛИ
  const [userRole, setUserRole] = useState('user');
  const [adminAccounts, setAdminAccounts] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);

  // 👉 ПОЛУЧАЕМ РОЛЬ ПРИ ЗАГРУЗКЕ КОМПОНЕНТА
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('user'));
      if (stored && stored.role) {
        setUserRole(stored.role);
      }
    } catch (e) {
      console.error('Ошибка чтения роли пользователя', e);
    }
  }, []);

  // 👉 ФУНКЦИЯ КОПИРОВАНИЯ КОДА
  const handleCopyCode = async () => {
    if (authChallenge?.pairingCode) {
      try {
        await navigator.clipboard.writeText(authChallenge.pairingCode);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000); // Возвращаем иконку через 2 секунды
      } catch (err) {
        console.error('Не удалось скопировать', err);
      }
    }
  };

  // 👉 НОВОЕ СОСТОЯНИЕ: 900 секунд = 15 минут
  const [authTimeLeft, setAuthTimeLeft] = useState(900); 

  // 👉 ФУНКЦИЯ ДЛЯ ФОРМАТИРОВАНИЯ (MM:SS)
  const formatTime = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
    const s = (totalSeconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

// 👉 ФУНКЦИЯ-ПОМОЩНИК: Парсит дату из БД и корректирует часовой пояс (+1 час)
  const parseDbDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    date.setHours(date.getHours() + 1); // 👈 Добавляем 1 час
    return date;
  };

  // 👉 ОБНОВЛЕННАЯ ФУНКЦИЯ ДЛЯ ПОДСЧЕТА ВРЕМЕНИ
  const getTimeAgo = (dateString) => {
    const date = parseDbDate(dateString);
    if (!date) return 'Нет данных';
    
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    // Защита: если из-за сдвига времени дата оказалась на пару секунд в будущем
    if (diffInSeconds < 0) return 'Только что';
    if (diffInSeconds < 60) return `${diffInSeconds} сек. назад`;
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes} мин. назад`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} ч. назад`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} дн. назад`;
  };

  // 👉 ОБРАБОТЧИК УДАЛЕНИЯ ДЛЯ АДМИНА
  const handleAdminDeleteAccount = async (accountId) => {
    if (!window.confirm(`Вы уверены, что хотите принудительно удалить аккаунт #${accountId}? Это разорвет соединение и очистит сессию.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${apiUrl}/api/whatsapp/admin/accounts/${accountId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
      });
      
      if (!response.ok) throw new Error('Не удалось удалить аккаунт');
      
      // Мгновенно убираем аккаунт из таблицы в интерфейсе
      setAdminAccounts((prev) => prev.filter(acc => acc.whatsapp_account_id !== accountId));
    } catch (error) {
      console.error('Ошибка удаления:', error);
      alert('Ошибка: ' + error.message);
    }
  };

  useEffect(() => {
    document.title = 'Настройки WhatsApp';
  }, []);





  useEffect(() => {
    if (activeTab !== 'status' && activeTab !== 'settings') return undefined;

    let cancelled = false;
    const loadStatus = async () => {
      setStatusLoading(true);
      try {
        const response = await fetch(`${apiUrl}/api/whatsapp/status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || 'Не удалось получить статус');
        if (!cancelled) {
          const nextBranches = result.branches || [];
          setBranches(nextBranches);
          setSelectedBranchId((current) => current || String(nextBranches[0]?.branchId || ''));
          if (authDialog && nextBranches.some((item) => String(item.branchId) === selectedBranchId && item.connected)) {
            setAuthDialog(false);
            // closeAuthDialog();
            setAuthChallenge(null);
            setAuthStarted(false);
          }
          setStatusError('');
        }
      } catch (loadError) {
        if (!cancelled) setStatusError(loadError.message || 'Сервер недоступен');
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    };

    loadStatus();
    const intervalId = setInterval(loadStatus, 5000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [activeTab, authDialog, selectedBranchId]);

const loadAuthChallenge = useCallback(async (accountId) => {
    try {
      const response = await fetch(`${apiUrl}/api/whatsapp/${accountId}/auth`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });

      // 1. Проверяем статус ответа. Если 500+ (воркер упал или загружается)
      if (!response.ok) {
        if (response.status >= 500) {
          throw new Error('Служба WhatsApp запускается или временно недоступна...');
        }
        // Если ошибка 400/401, пробуем безопасно достать JSON. Если там HTML, не падаем
        const result = await response.json().catch(() => ({}));
        throw new Error(result.error || 'Не удалось получить данные авторизации');
      }

      // 2. Если всё ОК, парсим данные
      const result = await response.json();
      setAuthChallenge(result.challenge);
      
      // Очищаем ошибку, если соединение восстановилось
      setAuthError('');
    } catch (error) {
      // 3. Ловим любую ошибку (сеть, 500, неверный формат) и мягко выводим
      // console.warn('Поллинг авторизации:', error.message);
      setAuthError(error.message || 'Ошибка соединения с сервером');
    }
  }, []);

  useEffect(() => {
    const accountId = branches.find((item) => String(item.branchId) === selectedBranchId)?.accountId;
    if (!authDialog || !accountId) return undefined;

    // Так как try...catch теперь внутри loadAuthChallenge, 
    // нам больше не нужен .catch(...) снаружи.
    const refreshChallenge = () => loadAuthChallenge(accountId);
    
    refreshChallenge();
    const intervalId = setInterval(refreshChallenge, 2000);
    return () => clearInterval(intervalId);
  }, [authDialog, branches, loadAuthChallenge, selectedBranchId]);

  const openAuthDialog = () => {
    setAuthMethod('qr');
    setAuthPhone('');
    setAuthChallenge(null);
    setQrImageLoaded(false);
    setAuthError('');
    setAuthStarted(false);
    setAuthDialog(true);
    setAuthTimeLeft(900);
  };

// 👉 ЛОГИКА ОТСЧЕТА ТАЙМЕРА (Исправленная)
  useEffect(() => {
    // Запускаем отсчет просто если диалог открыт (убрали !authStarted)
    if (!authDialog) return;

    const timer = setInterval(() => {
      setAuthTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0; // Останавливаемся на нуле
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [authDialog]); // Убрали authStarted из массива зависимостей

  // 👉 ЗАКРЫТИЕ ОКНА, ЕСЛИ ВРЕМЯ ВЫШЛО
  useEffect(() => {
    if (authDialog && authTimeLeft === 0) {
      closeAuthDialog(); // Вызываем вашу готовую функцию отмены
      setStatusError('Время на авторизацию истекло (15 минут). Пожалуйста, попробуйте снова.');
    }
  }, [authTimeLeft, authDialog]);

  // 👉 МГНОВЕННАЯ ОЧИСТКА ПРИ ЗАКРЫТИИ ИЛИ ОБНОВЛЕНИИ ВКЛАДКИ
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // Если окно авторизации открыто и процесс запущен
      if (authDialog && authStarted) {
        const branch = branches.find((item) => String(item.branchId) === selectedBranchId);
        if (branch) {
          // Флаг keepalive: true гарантирует, что браузер отправит запрос 
          // на отмену сессии даже в момент уничтожения страницы
          fetch(`${apiUrl}/api/whatsapp/branches/${branch.branchId}/cancel-auth`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
            keepalive: true 
          }).catch(() => {});
        }
      }
    };

    // Слушаем событие закрытия/обновления страницы
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Очищаем слушатель при размонтировании
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [authDialog, authStarted, branches, selectedBranchId]);

  // Загрузка данных для админ панели
  useEffect(() => {
    if (activeTab === 'admin' && userRole === 'admin') {
      let cancelled = false;
      const fetchAdminAccounts = async () => {
        setAdminLoading(true);
        try {
          const response = await fetch(`${apiUrl}/api/whatsapp/admin/accounts`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
          });
          if (!response.ok) throw new Error('Ошибка доступа');
          const data = await response.json();
          if (!cancelled) {
            setAdminAccounts(data.accounts || []);
          }
        } catch (error) {
          console.error(error);
        } finally {
          if (!cancelled) setAdminLoading(false);
        }
      };

      fetchAdminAccounts();
      return () => { cancelled = true; };
    }
  }, [activeTab, userRole]);

  const startAuthorization = useCallback(async () => {
    const branch = branches.find((item) => String(item.branchId) === selectedBranchId);
    if (!branch) return;
    if (authMethod === 'pairing' && authPhone.replace(/\D/g, '').length < 8) {
      setAuthError('Введите номер телефона с кодом страны');
      return;
    }

    setAuthLoading(true);
    setQrImageLoaded(false);
    setAuthError('');
    try {
      const response = await fetch(`${apiUrl}/api/whatsapp/branches/${branch.branchId}/authorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({ authType: authMethod, phoneNumber: authPhone }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Не удалось запустить авторизацию');
      setAuthStarted(true);
      setBranches((current) => current.map((item) => (
        item.branchId === branch.branchId ? { ...item, accountId: result.accountId } : item
      )));
      await loadAuthChallenge(result.accountId);
    } catch (startError) {
      setAuthError(startError.message || 'Сервис WhatsApp недоступен');
    } finally {
      setAuthLoading(false);
    }
  }, [authMethod, authPhone, branches, loadAuthChallenge, selectedBranchId]);

  // 👉 АВТОМАТИЧЕСКИЙ ПЕРЕЗАПУСК ПРИ СБРОСЕ КОДА СЕРВЕРОМ
  useEffect(() => {
    if (authChallenge?.pairingCode) {
      // 1. Как только код пришел, запоминаем его
      setPreviousCode(authChallenge.pairingCode);
    } else if (authStarted && previousCode && (!authChallenge || !authChallenge.pairingCode)) {
      // 2. Если код БЫЛ, но исчез (сервер ушел в реконнект), 
      // сбрасываем память и автоматически запрашиваем новый!
      setPreviousCode(null);
      startAuthorization();
    }
  }, [authChallenge, authStarted, previousCode, startAuthorization]);

  useEffect(() => {
    if (!authDialog || authStarted || authLoading || authError) return;
    // if (authMethod === 'pairing' && authPhone.replace(/\D/g, '').length < 8) return;
    if (authMethod === 'pairing') return;
    startAuthorization();
    
  }, [authDialog, authMethod, authPhone, authStarted, authLoading, authError, startAuthorization]);

  const handleRequestCode = () => {
    const cleanPhone = authPhone.replace(/\D/g, '');
    
    if (cleanPhone.length < 11) {
      setAuthError('Введите полный номер телефона.');
      return;
    }

    // Проверка валидности для номеров, начинающихся с 7 (РФ и РК)
    if (cleanPhone.startsWith('7')) {
      const secondDigit = cleanPhone[1];
      
      // Разрешенные вторые цифры: РК (7, 6) и РФ (9, 3, 4, 8)
      const allowedSecondDigits = ['7', '6', '9', '3', '4', '8'];
      
      if (!allowedSecondDigits.includes(secondDigit)) {
        setAuthError('Неверный код оператора для России или Казахстана.');
        return;
      }
    }

    setAuthError('');
    startAuthorization();
  };

  const handleAccountAction = async (action, accountId) => {
    setActionLoading(true);
    setStatusError('');
    try {
      const response = await fetch(`${apiUrl}/api/whatsapp/${accountId}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Операция не выполнена');
      setStatusError('');
    } catch (actionError) {
      setStatusError(actionError.message || 'Сервис WhatsApp недоступен');
    } finally {
      setActionLoading(false);
    }
  };

  const closeAuthDialog = async () => {
  setAuthDialog(false);
  // closeAuthDialog();
  
  // Если авторизация была запущена, отправляем запрос на отмену
  if (authStarted) {
    const branch = branches.find((item) => String(item.branchId) === selectedBranchId);
    if (branch) {
      try {
        await fetch(`${apiUrl}/api/whatsapp/branches/${branch.branchId}/cancel-auth`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        });
      } catch (err) {
        console.error('Ошибка при отмене авторизации:', err);
      }
    }
  }

  // Сбрасываем состояния
  setAuthChallenge(null);
  setAuthStarted(false);
  setQrImageLoaded(false);
  setAuthError('');
  };


  return (
    <main className={`whatsapp-page ${standalone ? 'whatsapp-page--standalone' : ''}`}>
      <header className="whatsapp-page__header">
        {!standalone && <Link className="whatsapp-back" to="/settings"><ArrowLeft size={18} /> Настройки</Link>}
        <div className="whatsapp-heading">
          <div className="whatsapp-heading__icon"><MessageCircle size={25} /></div>
          <div>
            {/* <p className="whatsapp-eyebrow">MESSAGE</p> */}
            <h1>Настройки WhatsApp</h1>
            <p>Управление WhatsApp для ответов, уведомлении, и прочие функции</p>
          </div>
        </div>
        <span className="whatsapp-status"><span /> Сервис готов</span>
      </header>

      <nav className="whatsapp-tabs" aria-label="Разделы настроек WhatsApp">
        <button
          className={`whatsapp-tab ${activeTab === 'status' ? 'whatsapp-tab--active' : ''}`}
          type="button"
          onClick={() => setActiveTab('status')}
        >
          Статус
        </button>
        <button
          className={`whatsapp-tab ${activeTab === 'settings' ? 'whatsapp-tab--active' : ''}`}
          type="button"
          onClick={() => setActiveTab('settings')}
        >
          Настройки
        </button>
        {/* 👉 НОВАЯ ВКЛАДКА ТОЛЬКО ДЛЯ АДМИНА */}
        {userRole === 'admin' && (
          <button
            className={`whatsapp-tab ${activeTab === 'admin' ? 'whatsapp-tab--active' : ''}`}
            type="button"
            onClick={() => setActiveTab('admin')}
          >
            Админ панель
          </button>
        )}
      </nav>


      {activeTab === 'admin' && userRole === 'admin' && (
        <section className="whatsapp-grid" style={{ gridTemplateColumns: '1fr' }}>
          <article className="whatsapp-panel">
            <div className="whatsapp-panel__topline"><MessageCircle size={18} /><span>Управление всеми аккаунтами</span></div>
            <h2>Админ панель WhatsApp</h2>
            <p className="whatsapp-muted">Здесь отображаются все подключенные аккаунты системы. Вы можете удалять сессии или редактировать настройки.</p>
            
            <div style={{ overflowX: 'auto', marginTop: '20px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ padding: '12px 8px', color: '#374151', fontWeight: 600 }}>ID / Сеть</th>
                    <th style={{ padding: '12px 8px', color: '#374151', fontWeight: 600 }}>Филиал</th>
                    <th style={{ padding: '12px 8px', color: '#374151', fontWeight: 600 }}>Номер телефона</th>
                    <th style={{ padding: '12px 8px', color: '#374151', fontWeight: 600 }}>Создана</th>
                    <th style={{ padding: '12px 8px', color: '#374151', fontWeight: 600 }}>Статус</th>
                    <th style={{ padding: '12px 8px', color: '#374151', fontWeight: 600, textAlign: 'right' }}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {adminLoading ? (
                    <tr>
                      <td colSpan="6" style={{ padding: '32px 16px', textAlign: 'center', color: '#6b7280' }}>
                        Загрузка аккаунтов...
                      </td>
                    </tr>
                  ) : adminAccounts.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ padding: '32px 16px', textAlign: 'center', color: '#6b7280' }}>
                        Аккаунты не найдены.
                      </td>
                    </tr>
                  ) : (
                    adminAccounts.map((acc) => (
                      <tr key={acc.whatsapp_account_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '12px 8px' }}>
                          <strong>#{acc.whatsapp_account_id}</strong>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>Сеть: {acc.network_name || 'Без сети'}</div>
                        </td>
                        <td style={{ padding: '12px 8px' }}>{acc.branch_name}</td>
                        <td style={{ padding: '12px 8px', fontFamily: 'monospace' }}>
                          {acc.phone_number || 'Не указан'}
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <div>
                            {acc.created_at 
                              ? parseDbDate(acc.created_at).toLocaleString('ru-RU', { 
                                  day: '2-digit', 
                                  month: '2-digit', 
                                  year: 'numeric',
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                }) 
                              : '—'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            {getTimeAgo(acc.created_at)}
                          </div>
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <span className={acc.status === 'connected' ? 'whatsapp-online' : 'whatsapp-offline'}>
                            {acc.status === 'connected' ? 'Подключена' : acc.status === 'pending' ? 'Ожидание' : 'Отключена'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                          <button 
                            className="whatsapp-action whatsapp-action--danger" 
                            style={{ padding: '6px 12px', fontSize: '13px', display: 'inline-flex' }}
                            onClick={() => handleAdminDeleteAccount(acc.whatsapp_account_id)}
                          >
                            Удалить
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </article>
        </section>
      )}

      {activeTab === 'settings' && (
        <section className="whatsapp-settings-layout">
          <article className="whatsapp-panel whatsapp-panel--accent whatsapp-settings-sidebar">
            <div className="whatsapp-panel__topline"><MessageCircle size={18} /><span>Филиал</span></div>
            <h2>Настройки</h2>
            <label className="whatsapp-select-field">
              Выберите филиал
              <select value={selectedBranchId} onChange={(event) => setSelectedBranchId(event.target.value)}>
                {branches.map((branch) => <option key={branch.branchId} value={branch.branchId}>{branch.branchName || `Филиал #${branch.branchId}`}</option>)}
              </select>
            </label>
            <p className="whatsapp-muted">Все параметры ниже применяются к выбранному филиалу.</p>
            <div className="whatsapp-sidebar-tabs" role="tablist" aria-label="Настройки филиала">
              <button
                className={`whatsapp-sidebar-tab ${settingsSection === 'details' ? 'whatsapp-sidebar-tab--active' : ''}`}
                type="button"
                role="tab"
                aria-selected={settingsSection === 'details'}
                onClick={() => setSettingsSection('details')}
              >
                Сведения
              </button>
              <button
                className={`whatsapp-sidebar-tab ${settingsSection === 'reply-template' ? 'whatsapp-sidebar-tab--active' : ''}`}
                type="button"
                role="tab"
                aria-selected={settingsSection === 'reply-template'}
                onClick={() => setSettingsSection('reply-template')}
              >
                Шаблон готового ответа
              </button>
              <button
                className={`whatsapp-sidebar-tab ${settingsSection === 'notifications' ? 'whatsapp-sidebar-tab--active' : ''}`}
                type="button"
                role="tab"
                aria-selected={settingsSection === 'notifications'}
                onClick={() => setSettingsSection('notifications')}
              >
                Уведомление
              </button>
              <button
                className={`whatsapp-sidebar-tab ${settingsSection === 'ai-options' ? 'whatsapp-sidebar-tab--active' : ''}`}
                type="button"
                role="tab"
                aria-selected={settingsSection === 'ai-options'}
                onClick={() => setSettingsSection('ai-options')}
              >
                Опция ответа
              </button>
            </div>
          </article>

          <div className="whatsapp-settings-content">
            {settingsSection === 'reply-template' && <WhatsAppRepliesEditor branches={branches} selectedBranchId={selectedBranchId} />}
            {settingsSection === 'notifications' && <WhatsAppAppointmentNotifications selectedBranchId={selectedBranchId} />}
            {settingsSection === 'ai-options' && <WhatsAppAIOptions selectedBranchId={selectedBranchId} />}
            {settingsSection === 'details' && <WhatsAppGeneralSettings selectedBranchId={selectedBranchId} />}
          </div>
        </section>
      )}
      {activeTab === 'status' && (
        <section className="whatsapp-grid">
          <article className="whatsapp-panel">
            <div className="whatsapp-panel__topline"><MessageCircle size={18} /><span>Филиалы</span></div>
            <h2>Статус WhatsApp</h2>
            <p className="whatsapp-muted">Выберите филиал, чтобы посмотреть подключение и дату последней авторизации.</p>
            {statusLoading && branches.length === 0 && <p className="whatsapp-muted">Загрузка статуса...</p>}
            {statusError && <div className="whatsapp-feedback whatsapp-feedback--error"><XCircle size={17} /> {statusError}</div>}
            <div className="whatsapp-branch-list">
              {branches.map((branch) => (
                <button
                  className={`whatsapp-branch ${String(branch.branchId) === selectedBranchId ? 'whatsapp-branch--active' : ''}`}
                  key={`${branch.branchId}-${branch.accountId || 'none'}`}
                  type="button"
                  onClick={() => setSelectedBranchId(String(branch.branchId))}
                >
                  <span>{branch.branchName || `Филиал #${branch.branchId}`}</span>
                  <small>{branch.networkName || 'Без сети'}</small>
                </button>
              ))}
            </div>
          </article>

          <article className="whatsapp-panel">
            {(() => {
              const branch = branches.find((item) => String(item.branchId) === selectedBranchId);
              if (!branch) return;
              const connected = branch?.connected;
              return (
                <>
                  <div className="whatsapp-panel__topline"><MessageCircle size={18} /><span>Подключение</span></div>
                  <h2>{branch?.branchName || 'Выберите филиал'}</h2>
                  {branch ? (
                    <>
                      <div className="whatsapp-detail"><span>Статус</span><strong className={connected ? 'whatsapp-online' : 'whatsapp-offline'}>{connected ? 'Подключена' : 'Не авторизована'}</strong></div>
                      <div className="whatsapp-detail"><span>Дата авторизации</span><strong>{branch.authorizationDate ? new Date(branch.authorizationDate).toLocaleString('ru-RU') : 'Нет данных'}</strong></div>
                      <div className="whatsapp-detail"><span>Номер</span><strong>{branch.phoneNumber || 'Не указан'}</strong></div>
                      {(
                        <button
                          className={`whatsapp-action ${connected ? 'whatsapp-action--danger' : ''}`}
                          type="button"
                          disabled={actionLoading}
                          onClick={connected ? () => handleAccountAction('logout', branch.accountId) : openAuthDialog}
                        >
                          {connected ? <LogOut size={17} /> : <LogIn size={17} />}
                          {actionLoading ? 'Выполняется...' : connected ? 'Выйти из аккаунта' : 'Авторизовать'}
                        </button>
                      )}
                    </>
                  ) : <p className="whatsapp-muted">Филиалы не найдены.</p>}
                </>
              );
            })()}
          </article>
        </section>
      )}

      

      {authDialog && (
        // <div className="whatsapp-modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setAuthDialog(false)}>
        <div className="whatsapp-modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && closeAuthDialog()}>
          <section className="whatsapp-modal" role="dialog" aria-modal="true" aria-labelledby="whatsapp-auth-title">
            <div className="whatsapp-modal__header">
              <div>
                <p className="whatsapp-eyebrow">АВТОРИЗАЦИЯ</p>
                <h2 id="whatsapp-auth-title">Подключить WhatsApp</h2>
                {/* <p>{branches.find((item) => String(item.branchId) === selectedBranchId)?.branchName || 'Выбранный филиал'}</p> */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
  <p style={{ margin: 0 }}>
    {branches.find((item) => String(item.branchId) === selectedBranchId)?.branchName || 'Выбранный филиал'}
  </p>
  
  {/* Убрали {authStarted && ...}, теперь таймер показывается всегда, пока открыто окно */}
  <span style={{ 
    fontSize: '13px', 
    color: '#ef4444', 
    fontWeight: '600', 
    backgroundColor: '#fee2e2', 
    padding: '2px 8px', 
    borderRadius: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px'
  }}>
    ⏳ Осталось: {formatTime(authTimeLeft)}
  </span>
</div>
              </div>
              {/* <button className="whatsapp-modal__close" type="button" onClick={() => setAuthDialog(false)} aria-label="Закрыть">×</button> */}
              {/* <button className="whatsapp-modal__close" type="button" onClick={() => closeAuthDialog} aria-label="Закрыть">×</button> */}
              <button className="whatsapp-modal__close" type="button" onClick={closeAuthDialog} aria-label="Закрыть">×</button>
            </div>
            <div className="whatsapp-auth-methods">
              <button className={authMethod === 'qr' ? 'whatsapp-method whatsapp-method--active' : 'whatsapp-method'} type="button" onClick={() => { setAuthMethod('qr'); setAuthStarted(false); setAuthChallenge(null); }}>QR-код</button>
              <button className={authMethod === 'pairing' ? 'whatsapp-method whatsapp-method--active' : 'whatsapp-method'} type="button" onClick={() => { setAuthMethod('pairing'); setAuthStarted(false); setAuthChallenge(null); }}>Код из устройства</button>
            </div>
            {authMethod === 'qr' ? (
              <div className="whatsapp-modal__instructions">
                <strong>Как авторизоваться по QR</strong>
                <ol>
                  <li>Откройте WhatsApp на телефоне.</li>
                  <li>Перейдите в «Настройки» → «Связанные устройства».</li>
                  <li>Нажмите «Связать устройство» и отсканируйте QR ниже.</li>
                </ol>
              </div>
            ) : (
              <>
                <div className="whatsapp-phone-field" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    Номер телефона
                  </label>
                  <PhoneInput
  country={'kz'} // 👈 Просто ставим 'kz' как стартовую страну
  value={authPhone}
  onChange={(phone) => {
    // 👈 Убрали ручное вычисление страны, оставляем только сохранение номера
    setAuthPhone(phone); 
    setAuthStarted(false);
    setAuthChallenge(null);
    setPreviousCode(null);
    setAuthError('');
  }}
  placeholder="+7 (700) 123-45-67"
  specialLabel="" 
  inputStyle={{
    width: '100%',
    height: '42px',
    fontSize: '14px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontFamily: 'inherit',
    paddingLeft: '48px'
  }}
  buttonStyle={{
    border: '1px solid #d1d5db',
    borderRight: 'none',
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '8px',
    backgroundColor: '#f9fafb'
  }}
/>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>
                    Выберите страну и введите номер. Лишние цифры вводить нельзя.
                  </span>

                  {/* 👉 КНОПКА ПОЛУЧЕНИЯ КОДА (Скрывается после старта) */}
                  {!authStarted && (
                    <button 
                      className="whatsapp-action" 
                      type="button" 
                      onClick={handleRequestCode}
                      disabled={authLoading}
                      style={{ marginTop: '8px', alignSelf: 'flex-start', padding: '8px 16px' }}
                    >
                      {authLoading ? 'Получение...' : 'Получить код'}
                    </button>
                  )}

                  {/* 👉 БЛОК С КОДОМ И КНОПКОЙ КОПИРОВАНИЯ */}
                  {authStarted && (
                    <div style={{ marginTop: '12px', minHeight: '48px', position: 'relative' }}>
                      {authChallenge?.pairingCode ? (
                        <>
                          {/* Оригинальный блок кода без изменений, чтобы сохранились стили CSS */}
                          <div className="whatsapp-pairing-code" aria-live="polite">
                            {authChallenge.pairingCode}
                          </div>
                          
                          {/* Кнопка "подвешена" абсолютно по правому краю */}
                          <button
                            type="button"
                            onClick={handleCopyCode}
                            style={{
                              position: 'absolute',
                              right: '16px',
                              top: '50%',
                              transform: 'translateY(-50%)', // Центрируем по вертикали
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              color: isCopied ? '#10b981' : '#6b7280',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '8px',
                              transition: 'color 0.2s'
                            }}
                            title="Скопировать код"
                          >
                            {isCopied ? <Check size={22} /> : <Copy size={22} />}
                          </button>
                        </>
                      ) : (
                        <div style={{ color: '#6b7280', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span className="whatsapp-qr-loader" style={{ position: 'relative', width: '16px', height: '16px', margin: 0 }}></span>
                          ⏳ Ожидание генерации кода...
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="whatsapp-modal__instructions">
                  <strong>Как авторизоваться по коду</strong>
                  <ol>
                    <li>Введите номер телефона, который подключён к WhatsApp.</li>
                    <li>Нажмите «Получить код» и дождитесь его генерации.</li>
                    <li>На телефоне откройте «Связанные устройства» → «Связать устройство» → «Использовать код».</li>
                    <li>Введите показанный код в WhatsApp.</li>
                  </ol>
                </div>
              </>
            )}
            {authLoading && <p className="whatsapp-modal__hint">Запускаем сессию...</p>}
            {authError && <div className="whatsapp-feedback whatsapp-feedback--error"><XCircle size={17} /> {authError}</div>}
            {/* {authMethod === 'pairing' && authChallenge?.pairingCode && <div className="whatsapp-pairing-code" aria-live="polite">{authChallenge.pairingCode}</div>} */}
            
            {authMethod === 'qr' && <div className="whatsapp-modal__qr">
              {authChallenge?.authType === 'qr' && !qrImageLoaded && <div className="whatsapp-qr-loader" aria-live="polite"><span />Загрузка QR-кода...</div>}
              {authChallenge?.authType === 'qr' && <img className={qrImageLoaded ? '' : 'whatsapp-qr-image--loading'} onLoad={() => setQrImageLoaded(true)} src={`${uploadUrl}/uploads/whatsapp-qr-${branches.find((item) => String(item.branchId) === selectedBranchId)?.accountId}.png?${Date.now()}`} alt="QR-код авторизации WhatsApp" />}
              {!authChallenge && <div className="whatsapp-qr-loader" aria-live="polite"><span />Подготовка QR-кода...</div>}
            </div>}
          </section>
        </div>
      )}
    </main>
  );
}
